import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { emitEvent } from '../../utilities/misc/events';
import { stylesBase } from './styles';
/**
 * @since 1.0.0
 * @status stable
 *
 * @tagname kemet-combo
 * @summary Allows the user to select a choice filtered through an Input. May only be used as a component of Field.
 *
 * @prop {string} slug - Uniquely identifies the component. Should match the slug used in a control.
 * @prop {array} options - An array of items listed for the combo box
 * @prop {boolean} show - Controls the display of the combo box
 *
 * @csspart combobox - The combo's container element.
 * @csspart listbox - The list element in the combo.
 *
 * @cssproperty --kemet-combo-width - The width of the combo box.
 * @cssproperty --kemet-combo-margin - The margins on the combo box.
 * @cssproperty --kemet-combo-max-height - The max height of the combo box.
 * @cssproperty --kemet-combo-border - The border of the combo box.
 * @cssproperty --kemet-combo-border-radius - The border radius of the combo box.
 * @cssproperty --kemet-combo-background-color - The background color of the combo box.
 * @cssproperty --kemet-combo-shadow - The shadow of the combo box.
 * @cssproperty --kemet-combo-hover-color - The hover item's text color.
 * @cssproperty --kemet-combo-hover-background-color - The hover item's background color.
 *
 * @event kemet-combo-selection - Fires when a selection has been made
 *
 */
let KemetCombo = class KemetCombo extends LitElement {
    constructor() {
        super(...arguments);
        this.options = [];
    }
    firstUpdated() {
        // standard properties
        this.field = this.closest('kemet-field');
        this.input = this.field.querySelector('[slot="input"]');
        // managed properties
        this.slug = this.field.slug || 'slug';
        // events listeners
        this.input.addEventListener('kemet-input-input', this.handleInput.bind(this));
        this.input.addEventListener('keydown', event => this.handleInputKeyDown(event));
    }
    render() {
        var _a;
        return html `
      <div
        role="combobox"
        part="combobox"
        aria-expanded=${this.show}
        aria-controls="${this.slug}-listbox"
        aria-haspopup="listbox"
        id="${this.slug}-combobox"
        aria-label="${(_a = this.field) === null || _a === void 0 ? void 0 : _a.label}"
      >
        <ul role="listbox" aria-labelledby="${this.slug}-label" id="${this.slug}-listbox" part="listbox">
          ${this.makeOptions()}
        </ul>
      </div>
    `;
    }
    makeOptions() {
        if (this.input) {
            this.filteredOptions = this.options.filter(option => { var _a; return option.toLowerCase().indexOf((_a = this.input.value) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== -1; });
            return this.filteredOptions.map((option, index) => html `<li
            id="${this.slug}-option-${index}"
            role="option"
            tabindex="0"
            aria-selected="false"
            @click=${(event) => this.handleSelection(event)}
            @keydown=${(event) => this.handleOptionKeyDown(event)}
          >
            ${option}
          </li>`);
        }
        return null;
    }
    handleInput(event) {
        this.makeOptions();
        this.show = event.detail.length > 0;
    }
    handleSelection(event) {
        const target = event.target;
        this.input.value = target.innerText;
        this.show = false;
        emitEvent(this, 'kemet-combo-selection', {
            element: target,
            text: target.innerText,
            id: target.getAttribute('id'),
        });
    }
    handleOptionKeyDown(event) {
        event.preventDefault();
        const target = event.target;
        if (event.code === 'ArrowDown') {
            const next = target.nextElementSibling;
            if (next) {
                next.focus();
            }
            else {
                this.shadowRoot.querySelector('li').focus();
            }
        }
        if (event.code === 'ArrowUp') {
            const previous = target.previousElementSibling;
            const lastChild = this.shadowRoot.querySelector('li:last-child');
            if (previous) {
                previous.focus();
            }
            else {
                lastChild.focus();
            }
        }
        if (event.code === 'Escape' || event.code === 'Tab') {
            this.show = false;
            this.input.focus();
        }
        if (event.code === 'Enter' || event.code === 'Space') {
            this.handleSelection(event);
        }
    }
    handleInputKeyDown(event) {
        if (event.code === 'ArrowDown') {
            this.shadowRoot.querySelector('li').focus();
        }
        if (event.code === 'Escape') {
            this.show = false;
        }
    }
};
KemetCombo.styles = [stylesBase];
__decorate([
    property({ type: String })
], KemetCombo.prototype, "slug", void 0);
__decorate([
    property({ type: Array })
], KemetCombo.prototype, "options", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetCombo.prototype, "show", void 0);
__decorate([
    state()
], KemetCombo.prototype, "filteredOptions", void 0);
__decorate([
    state()
], KemetCombo.prototype, "field", void 0);
__decorate([
    state()
], KemetCombo.prototype, "input", void 0);
KemetCombo = __decorate([
    customElement('kemet-combo')
], KemetCombo);
export default KemetCombo;
//# sourceMappingURL=kemet-combo.js.map